import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { Mermaid } from "@/components/Mermaid";
import { toast } from "sonner";
import { Lock, Download, Sigma } from "lucide-react";

const Pill = ({ children }) => (
  <span className="inline-block rounded-full bg-ek-purple/10 px-2.5 py-0.5 text-xs font-medium text-ek-purple">{children}</span>
);

function KeyValueTable({ obj }) {
  return (
    <table className="mt-3 w-full max-w-md text-left text-sm">
      <tbody>
        {Object.entries(obj).map(([k, v]) => (
          <tr key={k} className="border-t border-black/5">
            <td className="py-1.5 pr-4 font-medium text-ek-ink/70">{k}</td>
            <td className="py-1.5 text-ek-ink/65">{typeof v === "number" ? v : String(v)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Section({ s }) {
  return (
    <section data-testid={`methodology-section-${s.id}`} className="rounded-md border border-black/5 bg-white p-6">
      <h3 className="font-display text-xl font-semibold text-ek-ink">{s.title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-ek-ink/65">{s.summary}</p>

      {s.formula && (
        <div className="mt-4 flex items-start gap-2 rounded-md bg-ek-ink px-4 py-3">
          <Sigma size={16} className="mt-0.5 flex-none text-ek-lilac" />
          <code className="font-mono text-sm leading-relaxed text-ek-lilac">{s.formula}</code>
        </div>
      )}

      {s.detail && (
        <ul className="mt-4 space-y-1.5">
          {s.detail.map((d, i) => (
            <li key={i} className="flex gap-2 text-sm text-ek-ink/65">
              <span className="mt-2 h-1 w-1 flex-none rounded-full bg-ek-purple" />
              <span className="leading-relaxed">{d}</span>
            </li>
          ))}
        </ul>
      )}

      {s.components && (
        <div className="mt-4 flex flex-wrap gap-2">
          {s.components.map((c) => (
            <Pill key={c.key}>{c.label} · {c.weight}%</Pill>
          ))}
        </div>
      )}

      {s.weights && !s.components && <KeyValueTable obj={s.weights} />}
      {s.fx_rates && (
        <div className="mt-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-ek-ink/65">Reference FX (→ GBP)</p>
          <KeyValueTable obj={s.fx_rates} />
        </div>
      )}

      {s.bands && (
        <div className="mt-4 flex flex-wrap gap-2">
          {s.bands.map((b) => (
            <span key={b.label} className="rounded-md border border-black/10 px-3 py-1 text-xs text-ek-ink/60">
              &lt; {b.upto}: <span className="font-semibold text-ek-ink">{b.label}</span>
            </span>
          ))}
        </div>
      )}

      {s.themes && (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {s.themes.map((t) => (
            <span key={t.id} className="rounded-full bg-ek-canvas px-2.5 py-0.5 text-xs text-ek-ink/65">{t.name}</span>
          ))}
        </div>
      )}

      {s.worked_example && (
        <p className="mt-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="font-semibold">Worked example — </span>{s.worked_example}
        </p>
      )}
    </section>
  );
}

export default function Methodology() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/methodology").then(({ data }) => setData(data)).catch(() => setError(true));
  }, []);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/secure-docs/${data.pdf}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        const a = document.createElement("a");
        a.href = url;
        a.download = data.pdf;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("PDF not available");
    }
  };

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Methodology (proprietary)" description="Ekselens-proprietary engine methodology — administrators only." path="/methodology" />
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-10" data-testid="methodology-page">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="ek-eyebrow flex items-center gap-1.5"><Lock size={13} /> Ekselens proprietary</p>
            <h1 className="mt-1 font-display text-3xl font-bold text-ek-ink">The engine — methodology reference</h1>
            <p className="mt-2 max-w-2xl text-sm text-ek-ink/65">
              The exact weightings, formulas and model logic behind every ERIO score. Administrators only.
            </p>
          </div>
          {data?.pdf && (
            <button onClick={downloadPdf} data-testid="methodology-download-pdf" className="ek-btn">
              <Download size={16} /> Proprietary PDF
            </button>
          )}
        </div>

        {data?.classification && (
          <div className="mt-6 rounded-md border border-red-200 bg-red-50 px-5 py-3 text-sm font-semibold text-red-700" data-testid="methodology-classification">
            {data.classification}
          </div>
        )}

        {error && (
          <p className="mt-8 rounded-md border border-black/5 bg-white p-6 text-sm text-ek-ink/65" data-testid="methodology-no-access">
            This proprietary reference is restricted to administrators.
          </p>
        )}

        {data && (
          <>
            <div className="mt-8 space-y-6">
              {data.sections.map((s) => <Section key={s.id} s={s} />)}
            </div>

            <h2 className="ek-eyebrow mt-12">Engine diagrams</h2>
            <div className="mt-4 space-y-8">
              {data.diagrams.map((d) => (
                <div key={d.title} data-testid={`methodology-diagram-${d.title.split(" ")[0].toLowerCase()}`}>
                  <p className="mb-2 text-sm font-semibold text-ek-ink/70">{d.title}</p>
                  <Mermaid chart={d.definition} />
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
